import React, { Component } from 'react'
import { message, Button, Card, Icon, Tooltip, Divider, List, Statistic, Row, Col } from 'antd'
import { TenantService } from 'connection/TenantService'
import { getCaller } from 'services/CeberusApiService'
import { ConfigurationRequest } from 'models/request/ConfigurationRequest'
import { RedisRequest } from 'models/request/RedisRequest'
import { resyncRedisDistribution, getSyncStateRedisDistribution, restartOrleansDistributionGrains } from 'services/CeberusApiService'
import { OrleansRequest } from 'models/request/OrleansRequest'
import { Constants } from 'utils/constants'
import { BaseRequest } from 'models/request/BaseRequest'

export class DeskDistribution extends Component<BaseRequest> {

  state = {
    isLoading: false,
    distributionType: '',
    syncState: null
  }

  setComponentLoading(isLoading) {
    this.setState({ isLoading })
  }

  componentDidMount() {
    this.checkClusterAndDistributionType()
  }

  async checkClusterAndDistributionType() {
    const distributionType = await this.getDistributionType({ identity: this.props.BotIdentifier })
    this.setState({ distributionType, syncState: null })
  }

  async getDistributionType({ identity }) {
    try {
      const configurationRequest = new ConfigurationRequest()
      configurationRequest.ClusterName = TenantService.getSelectedTenant().name
      configurationRequest.BotIdentifier = identity
      configurationRequest.Caller = identity
      configurationRequest.Owner = 'postmaster@desk.msging.net'
      const { DistributionType, DefaultProvider } = await getCaller(configurationRequest)
      if(DefaultProvider !== 'Lime') {
        return ''
      }
      if(!DistributionType) {
        return Constants.DISTRIBUTION_TYPE_REDIS.toLowerCase()
      }
      return DistributionType.toLowerCase()
    } catch (error) {
      return Constants.DISTRIBUTION_TYPE_SQL.toLowerCase()
    }
  }

  async resyncRedisDistribution() {
    try {
      this.setComponentLoading(true)
      const redisRequest = new RedisRequest()
      redisRequest.ClusterName = this.props.ClusterName
      redisRequest.BotIdentifier = this.props.BotIdentifier
      await resyncRedisDistribution(redisRequest)
      message.success('Redis Distribution resynced', 3)
    } catch (e) {
      message.error('Command to resync failed', 3)
      console.error(e)
    } finally {
      this.setComponentLoading(false)
    }
  }

  async getSyncStateRedisDistribution() {
    try {
      this.setComponentLoading(true)
      const redisRequest = new RedisRequest()
      redisRequest.ClusterName = this.props.ClusterName
      redisRequest.BotIdentifier = this.props.BotIdentifier
      const resource = await getSyncStateRedisDistribution(redisRequest)
      this.setState({ syncState: resource })
    } catch (e) {
      message.error('Command to check redis failed', 3)
      console.error(e)
    } finally {
      this.setComponentLoading(false)
    }
  }

  async restartOrleansDistributionGrainsAsync() {
    try {
      this.setComponentLoading(true)
      const orleansRequest = new OrleansRequest()
      orleansRequest.ClusterName = this.props.ClusterName
      orleansRequest.BotIdentifier = this.props.BotIdentifier
      await restartOrleansDistributionGrains(orleansRequest)
      this.setState({ syncState: true })
      message.success('Orleans Distribution Grains restarted', 3)
    } catch (e) {
      this.setState({ syncState: false })
      message.error('Command to restart failed', 3)
      console.error(e)
    } finally {
      this.setComponentLoading(false)
    }
  }

  render() {
    return (
      <div>
        <Card title="Automatic Ticket Distribution" loading={this.state.isLoading}>
          {this.state.distributionType === Constants.DISTRIBUTION_TYPE_REDIS.toLowerCase() &&
            <div>
              <h4>
                <Tooltip title="This command momentaneously stops the distribution in order to correctly check the storage">
                  <Icon type="warning" className="mr2" />
                  Check Redis Distribution:
                </Tooltip>
                <Button className="ml3" type="primary" icon="search" onClick={() => this.getSyncStateRedisDistribution()} loading={this.state.isLoading}>
                  Check
                </Button>
              </h4>
              { this.state.syncState
                &&
                <div>
                  <Row className="mt3">
                    <Col span={24}>
                      <Statistic title="Sync Status" value={this.state.syncState.isSynced} valueStyle={this.state.syncState.isSynced ? { color: '#3f8600' } : { color: '#cf1322' }}/>
                    </Col>
                  </Row>
                  <Row className="mt3">
                    <Col span={12}>
                      <Statistic title="#Tickets on Redis" value={this.state.syncState.redisTickets} valueStyle={this.state.syncState.isSynced ? { color: '#3f8600' } : { color: '#cf1322' }}/>
                    </Col>
                    <Col span={12}>
                      <Statistic title="#Tickets on Sql" value={this.state.syncState.sqlTickets} valueStyle={this.state.syncState.isSynced ? { color: '#3f8600' } : { color: '#cf1322' }}/>
                    </Col>
                  </Row>
                  <Row className="mt3">
                    <h4>Tickets out of Sync</h4>
                    <List
                      bordered
                      dataSource={this.state.syncState.ticketsOutOfSync || []}
                      renderItem={item => (<List.Item><b>Id: </b> {item}</List.Item>)}/>
                  </Row>
                  <Row className="mt3">
                    <h4>Agents out of Sync</h4>
                    <List
                      bordered
                      dataSource={this.state.syncState.agentsOutOfSync?.identity || []}
                      renderItem={item => (<List.Item><b>Identity: </b> {item}</List.Item>)}/>
                  </Row>
                </div>
              }
              <Divider />
              {this.state.syncState &&
              !this.state.syncState.isSynced &&
              <h4>
                <Tooltip title="This will guarantee the distribution is correct by recreating the storage for redis distribution">
                  <Icon type="info-circle" className="mr2" />
                  Resync Redis Distribution:
                </Tooltip>
                <Button className="ml3" type="primary" icon="sync" onClick={() => this.resyncRedisDistribution()} loading={this.state.isLoading}>
                  Resync
                </Button>
              </h4>
              }
            </div>
          }
          {this.state.distributionType === Constants.DISTRIBUTION_TYPE_SQL.toLowerCase() &&
            <span>This chatbot uses <b>Sql</b> Distribution</span>
          }
          {this.state.distributionType === Constants.DISTRIBUTION_TYPE_ORLEANS.toLowerCase() &&
            <div>
              <h4>
                <Tooltip title="This will guarantee the distribution is correct by recreating the grains for Orleans distribution">
                  <Icon type="info-circle" className="mr2" />
                  Restart Orleans Distribution Grains:
                </Tooltip>
                <Button className="ml3" type="primary" icon="sync" onClick={() => this.restartOrleansDistributionGrainsAsync()} loading={this.state.isLoading}>
                  Restart
                </Button>
              </h4>
              <Divider />
              { this.state.syncState
              &&
              <div id="syncStateSuccess">
                <h4>
                  Orleans Distribution Grains restarted successfully!
                </h4>
              </div>
              }
              { this.state.syncState != null && !this.state.syncState
              &&
              <div>
                <h4 id="syncStateFail">
                  Fail on restart Orleans Distribution Grains!
                </h4>
              </div>
              }
            </div>
          }
          {this.state.distributionType === '' &&
            <span>This chatbot <b>doesn't use any distribution</b> as it does not uses Blip Desk Provider</span>
          }
        </Card>
      </div>
    )
  }
}